import './index.css';
import moment from 'moment';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import TitleRoute from '../../components/titleRoute';
import Communication from '../../components/Activities/Communication';
import { getInboxConversations, getInboxUnreadCount, setInboxFilters, getInboxContact, markInboxRead, markInboxUnread, setInboxActiveContactId } from '../../store/Inbox';

const Inbox = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const searchTimer = useRef(null);
  const { currentWorkspace } = useSelector(state => state['Settings']);
  const { conversations, total, connectedChannels, filters, loading, activeContactId, activeContact, contactLoading } = useSelector(state => state['Inbox']);

  // The conversation deep-linked in the URL (?conversation=<contactId>) — drives open-on-reload
  const urlContactId = new URLSearchParams(location['search']).get('conversation');

  // Open the selected contact's single page in a new browser tab
  const openContact = () => {
    if (!activeContact?.['_id']) return;
    const wsId = currentWorkspace?.['_id'];
    const url = wsId ? `/${wsId}/contacts/${activeContact['_id']}` : `/contacts/${activeContact['_id']}`;
    window.open(url, '_blank', 'noopener');
  };

  // Initial load: conversations + sidebar unread total
  useEffect(() => {
    dispatch(getInboxUnreadCount());
    dispatch(getInboxConversations());
  }, [dispatch]);

  // Refetch whenever the scoping filters change (not on paging skip — that appends)
  useEffect(() => {
    dispatch(getInboxConversations());
  }, [dispatch, filters['channel'], filters['account'], filters['status'], filters['search']]);

  // URL → state: open the deep-linked conversation on load / reload / back-forward navigation.
  // Does NOT mark read — only an explicit click does that, so a deep link preserves unread state.
  useEffect(() => {
    if (urlContactId && urlContactId !== activeContactId) {
      dispatch(setInboxActiveContactId(urlContactId));
      dispatch(getInboxContact(urlContactId));
    }
  }, [dispatch, urlContactId]);

  // Tab order: Email first, then SMS, then WhatsApp (matches the Communication panel convention).
  const channelKeys = Object.keys(connectedChannels || {}).sort((a, b) => CHANNEL_ORDER.indexOf(a) - CHANNEL_ORDER.indexOf(b));
  const accounts = filters['channel'] !== 'all' ? (connectedChannels[filters['channel']]?.['accounts'] || []) : [];

  const handleChannel = (channel) => dispatch(setInboxFilters({ channel, account: null, skip: 0 }));
  const handleAccount = (account) => dispatch(setInboxFilters({ account: account || null, skip: 0 }));
  const handleStatus = (status) => dispatch(setInboxFilters({ status, skip: 0 }));

  const handleSearch = (value) => {
    if (searchTimer['current']) clearTimeout(searchTimer['current']);
    searchTimer['current'] = setTimeout(() => dispatch(setInboxFilters({ search: value, skip: 0 })), 350);
  };

  const handleSelect = (row) => {
    // Already open — do nothing. Re-selecting must NOT re-mark read, or a "mark unread" on the
    // currently-open conversation would be instantly undone the moment it re-renders / is re-clicked.
    if (activeContactId === row['contactId']) return;
    dispatch(setInboxActiveContactId(row['contactId']));
    dispatch(getInboxContact(row['contactId']));
    if (row['unreadCount'] > 0) dispatch(markInboxRead(row['contactId']));
    // Reflect the open conversation in the URL so it survives reload / can be shared
    const params = new URLSearchParams(location['search']);
    params.set('conversation', row['contactId']);
    history.push({ pathname: location['pathname'], search: params.toString() });
  };

  // Toggle a row's read/unread state without opening it (stops the row's select handler)
  const handleMarkToggle = (e, row) => {
    e.stopPropagation();
    if (row['unreadCount'] > 0) dispatch(markInboxRead(row['contactId']));
    else dispatch(markInboxUnread(row['contactId']));
  };

  const handleRowKey = (e, row) => {
    if (e['key'] === 'Enter' || e['key'] === ' ') { e.preventDefault(); handleSelect(row); }
  };

  return (
    <div className="content">
      <div className="main-container">
        <div className="bottom-content-area">
          <div className="tabs-content">
            <div className="content">
              <div className="inbox-area">

                <div className="header-title-area">
                  <div className="left-area">
                    <TitleRoute />
                    {total > 0 && <p className="records-title">{total} conversations</p>}
                  </div>
                </div>

                <div className="inbox-panes">

                  {/* Left pane — conversation list */}
                  <div className="inbox-list-pane">
                    <div className="inbox-selector">
                      <div className="inbox-channel-tabs">
                        <button className={`inbox-channel-tab ${filters['channel'] === 'all' ? 'active' : ''}`} onClick={() => handleChannel('all')}>All</button>
                        {channelKeys.map(ch => (
                          <button key={ch} className={`inbox-channel-tab ${filters['channel'] === ch ? 'active' : ''}`} onClick={() => handleChannel(ch)}>
                            <i className={CHANNEL_ICONS[ch] || 'fa fa-comment'} /> {CHANNEL_LABELS[ch] || ch}
                          </button>
                        ))}
                      </div>
                      {filters['channel'] !== 'all' && accounts.length > 0 && (
                        <select className="inbox-account-select" value={filters['account'] || ''} onChange={e => handleAccount(e['target']['value'])}>
                          <option value="">All accounts</option>
                          {accounts.map(a => <option key={a['matchValue']} value={a['matchValue']}>{a['label']}</option>)}
                        </select>
                      )}
                    </div>

                    <div className="inbox-status-tabs">
                      <button className={`inbox-status-tab ${filters['status'] === 'all' ? 'active' : ''}`} onClick={() => handleStatus('all')}>All</button>
                      <button className={`inbox-status-tab ${filters['status'] === 'unread' ? 'active' : ''}`} onClick={() => handleStatus('unread')}>Unread</button>
                      <button className={`inbox-status-tab ${filters['status'] === 'incoming' ? 'active' : ''}`} onClick={() => handleStatus('incoming')}>Incoming</button>
                    </div>

                    <div className="inbox-search">
                      <i className="fa fa-search inbox-search-icon" />
                      <input type="text" placeholder="Search conversations" defaultValue={filters['search']} onChange={e => handleSearch(e['target']['value'])} />
                    </div>

                    <div className="inbox-list">
                      {loading && <div className="inbox-empty"><i className="fa fa-spinner fa-spin" /> Loading…</div>}
                      {!loading && conversations.length === 0 && <div className="inbox-empty">No conversations</div>}
                      {!loading && conversations.map(row => {
                        const isIncoming = row['lastMessage']['direction'] === 'inbound';
                        const isUnread = row['unreadCount'] > 0;
                        return (
                          <div key={row['contactId']} role="button" tabIndex={0} className={`inbox-row ${activeContactId === row['contactId'] ? 'active' : ''} ${isIncoming ? 'incoming' : ''}`} onClick={() => handleSelect(row)} onKeyDown={e => handleRowKey(e, row)}>
                            <div className="inbox-row-avatar">{initials(row['contact']['name'])}</div>
                            <div className="inbox-row-main">
                              <div className="inbox-row-top">
                                <span className={`inbox-row-name ${isUnread ? 'unread' : ''}`}>{row['contact']['name']}</span>
                                <span className="inbox-row-time">{relativeTime(row['lastMessage']['timestamp'])}</span>
                              </div>
                              <div className="inbox-row-bottom">
                                <span className="inbox-row-snippet">
                                  <i className={`inbox-row-dir ${isIncoming ? 'incoming fa fa-arrow-down' : 'outgoing fa fa-arrow-up'}`} title={isIncoming ? 'Incoming' : 'Outgoing'} />
                                  <i className={`${CHANNEL_ICONS[row['lastMessage']['channel']] || 'fa fa-comment'} inbox-row-channel`} />
                                  {row['lastMessage']['snippet']}
                                </span>
                                {isUnread && <span className="inbox-row-badge">{row['unreadCount']}</span>}
                              </div>
                            </div>
                            {isIncoming && (
                              <i
                                className={`inbox-row-mark fa ${isUnread ? 'fa-envelope-open-o' : 'fa-envelope'} ${isUnread ? 'is-unread' : ''}`}
                                title={isUnread ? 'Mark as read' : 'Mark as unread'}
                                onClick={e => handleMarkToggle(e, row)}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Middle pane — embedded Communication panel */}
                  <div className="inbox-thread-pane">
                    {!activeContactId && <div className="inbox-placeholder"><i className="fa fa-comments" /><p>Select a conversation</p></div>}
                    {activeContactId && contactLoading && <div className="inbox-placeholder"><i className="fa fa-spinner fa-spin" /></div>}
                    {activeContactId && !contactLoading && activeContact && (
                      <div className="single-deal-inner">
                        <div className="left-side">
                          <Communication embedded singlePageData={activeContact} objectType="contacts" initialTab="email" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right pane — contact details */}
                  <div className="inbox-contact-pane">
                    {activeContact ? (
                      <div className="inbox-contact-card">
                        <div className="inbox-contact-avatar">{initials(contactName(activeContact))}</div>
                        <h4 className="inbox-contact-name inbox-contact-name-link" onClick={openContact} title="Open contact in new tab">{contactName(activeContact)} <i className="fa fa-external-link" /></h4>

                        {activeContact['email'] && <div className="inbox-contact-field"><i className="fa fa-envelope" /> {activeContact['email']}</div>}
                        {activeContact['phone'] && <div className="inbox-contact-field"><i className="fa fa-phone" /> {activeContact['phone']}</div>}
                        {(activeContact['position'] || activeContact['company']) && <div className="inbox-contact-field"><i className="fa fa-briefcase" /> {[activeContact['position'], activeContact['company']].filter(Boolean).join(' · ')}</div>}
                        {activeContact['department'] && <div className="inbox-contact-field"><i className="fa fa-sitemap" /> {activeContact['department']}</div>}
                        {locationText(activeContact['location']) && <div className="inbox-contact-field"><i className="fa fa-map-marker" /> {locationText(activeContact['location'])}</div>}
                        {(activeContact['linkedinUrl'] || activeContact['linkedin']) && <a className="inbox-contact-field inbox-contact-link" href={withProtocol(activeContact['linkedinUrl'] || activeContact['linkedin'])} target="_blank" rel="noreferrer"><i className="fab fa-linkedin" /> LinkedIn</a>}
                        {activeContact['twitter'] && <a className="inbox-contact-field inbox-contact-link" href={withProtocol(activeContact['twitter'])} target="_blank" rel="noreferrer"><i className="fab fa-twitter" /> Twitter</a>}
                        {activeContact['website'] && <a className="inbox-contact-field inbox-contact-link" href={withProtocol(activeContact['website'])} target="_blank" rel="noreferrer"><i className="fa fa-globe" /> {activeContact['website']}</a>}
                        {activeContact['ownerId'] && <div className="inbox-contact-field"><i className="fa fa-user" /> {ownerName(activeContact['ownerId'])}</div>}
                        {activeContact['createdAt'] && <div className="inbox-contact-field"><i className="fa fa-calendar" /> Added {moment(activeContact['createdAt']).format('MMM D, YYYY')}</div>}
                        {activeContact['lastContactedAt'] && <div className="inbox-contact-field"><i className="fa fa-clock" /> Last contacted {moment(activeContact['lastContactedAt']).format('MMM D, YYYY')}</div>}

                        {Array.isArray(activeContact['tags']) && activeContact['tags'].length > 0 && (
                          <div className="inbox-contact-section">
                            <span className="inbox-contact-section-title">Tags</span>
                            <div className="inbox-contact-tags">
                              {activeContact['tags'].map((t, i) => {
                                const name = typeof t === 'string' ? t : t['name'];
                                const color = typeof t === 'string' ? null : t['color'];
                                const style = color ? { backgroundColor: color + '1a', color } : {};
                                return <span key={i} className="inbox-contact-tag" style={style}>{name}</span>;
                              })}
                            </div>
                          </div>
                        )}

                        {Array.isArray(activeContact['companies']) && activeContact['companies'].length > 0 && (
                          <div className="inbox-contact-section">
                            <span className="inbox-contact-section-title">Companies</span>
                            {activeContact['companies'].map((c, i) => (
                              <div key={i} className="inbox-related-item"><i className="fa fa-building-o" /> <span>{c['name']}</span></div>
                            ))}
                          </div>
                        )}

                        {Array.isArray(activeContact['deals']) && activeContact['deals'].length > 0 && (
                          <div className="inbox-contact-section">
                            <span className="inbox-contact-section-title">Deals</span>
                            {activeContact['deals'].map((d, i) => (
                              <div key={i} className="inbox-related-item">
                                <i className="fa fa-handshake-o" />
                                <span className="inbox-related-name">{d['title']}</span>
                                {d['amount'] != null && <span className="inbox-related-meta">{formatAmount(d['amount'])}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : <div className="inbox-placeholder inbox-contact-empty"><i className="fa fa-user" /></div>}
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Full FontAwesome 5 class per channel — WhatsApp is a brand icon (fab), the rest are solid (fa/fas).
const CHANNEL_ICONS = { email: 'fa fa-envelope', sms: 'fa fa-comment', whatsapp: 'fab fa-whatsapp' };
const CHANNEL_LABELS = { email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp' };
const CHANNEL_ORDER = ['email', 'sms', 'whatsapp'];

// Two-letter initials from a display name
function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
}

// Contact display name from a contact object
function contactName(c) {
  return `${c['firstName'] || ''} ${c['lastName'] || ''}`.trim() || c['email'] || c['phone'] || 'Contact';
}

// Owner display name (ownerId is populated to { firstName, lastName })
function ownerName(owner) {
  if (!owner || typeof owner !== 'object') return 'Unassigned';
  return `${owner['firstName'] || ''} ${owner['lastName'] || ''}`.trim() || 'Unassigned';
}

// Compact currency-ish amount for deal rows
function formatAmount(amount) {
  const n = Number(amount);
  if (Number.isNaN(n)) return '';
  return '$' + n.toLocaleString();
}

// Join the contact's location parts (city, state, country) into one line
function locationText(loc) {
  if (!loc || typeof loc !== 'object') return '';
  return [loc['city'], loc['state'], loc['country']].filter(Boolean).join(', ');
}

// Ensure outbound links have a protocol so target=_blank works
function withProtocol(url) {
  if (!url) return '#';
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

// Compact relative timestamp (e.g. "2m", "3h", "Jun 4")
function relativeTime(ts) {
  if (!ts) return '';
  const m = moment(ts);
  const diffHours = moment().diff(m, 'hours');
  if (diffHours < 24) return m.fromNow(true);
  return m.format('MMM D');
}

export default Inbox;
